import type {
  AppEvent, ConsumptionMethodRecord, MethodLogMode, UsageLoggedPayloadV2,
  UsageSessionEndedPayload,
} from '../core/types.js';

export interface NormalizedUsage {
  eventIds: string[];
  habitId: string;
  planId?: string | undefined;
  methodId?: string | undefined;
  methodKind?: string | undefined;
  occurredAt: string;
  effectiveStartAt: string;
  effectiveEndAt: string;
  quantity: number;
  domainUnits: number;
  logMode: MethodLogMode;
  source: 'event' | 'merged_session';
}

interface ResolvedEvent {
  ev: AppEvent;
  payload: UsageLoggedPayloadV2;
  methodId: string | undefined;
  methodKind: string | undefined;
  logMode: MethodLogMode;
  defaultSessionMinutes: number;
  mergeWindowMinutes: number;
  quantity: number;
  domainUnits: number;
}

function pickMethodMatch(
  payload: UsageLoggedPayloadV2,
  methods: ConsumptionMethodRecord[],
): ConsumptionMethodRecord | undefined {
  if (payload.methodId) {
    const byId = methods.find((m) => m.id === payload.methodId);
    if (byId) return byId;
  }
  if (payload.methodKind) {
    return methods.find((m) => m.kind === payload.methodKind);
  }
  return undefined;
}

function asPayload(ev: AppEvent): UsageLoggedPayloadV2 {
  return (ev.payload ?? {}) as UsageLoggedPayloadV2;
}

function resolveEvent(
  ev: AppEvent,
  methods: ConsumptionMethodRecord[],
): ResolvedEvent {
  const payload = asPayload(ev);
  const method = pickMethodMatch(payload, methods);
  const logMode: MethodLogMode = payload.logMode ?? method?.defaultLogMode ?? 'instant';
  const defaultSessionMinutes = payload.defaultSessionMinutes
    ?? method?.defaultSessionMinutes
    ?? (logMode === 'session' ? 5 : logMode === 'extended_session' ? 45 : 0);
  const mergeWindowMinutes = payload.mergeWindowMinutes
    ?? method?.mergeWindowMinutes
    ?? (logMode === 'session' ? 10 : 0);
  const quantity = typeof payload.quantity === 'number' ? payload.quantity : 1;
  const domainUnits = typeof payload.domainUnits === 'number'
    ? payload.domainUnits
    : (method?.defaultDomainUnits ?? 1);
  return {
    ev,
    payload,
    methodId: method?.id ?? payload.methodId,
    methodKind: method?.kind ?? payload.methodKind,
    logMode,
    defaultSessionMinutes,
    mergeWindowMinutes,
    quantity,
    domainUnits,
  };
}

function compareEvents(a: AppEvent, b: AppEvent): number {
  if (a.occurredAt !== b.occurredAt) return a.occurredAt < b.occurredAt ? -1 : 1;
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function addMinutes(iso: string, minutes: number): string {
  return new Date(new Date(iso).getTime() + minutes * 60_000).toISOString();
}

function makeInstant(r: ResolvedEvent): NormalizedUsage {
  const out: NormalizedUsage = {
    eventIds: [r.ev.id],
    habitId: r.ev.habitId ?? '',
    occurredAt: r.ev.occurredAt,
    effectiveStartAt: r.ev.occurredAt,
    effectiveEndAt: r.ev.occurredAt,
    quantity: r.quantity,
    domainUnits: r.domainUnits,
    logMode: 'instant',
    source: 'event',
  };
  if (r.ev.planId !== undefined) out.planId = r.ev.planId;
  if (r.methodId !== undefined) out.methodId = r.methodId;
  if (r.methodKind !== undefined) out.methodKind = r.methodKind;
  return out;
}

function makeExtended(r: ResolvedEvent): NormalizedUsage {
  const out: NormalizedUsage = {
    eventIds: [r.ev.id],
    habitId: r.ev.habitId ?? '',
    occurredAt: r.ev.occurredAt,
    effectiveStartAt: r.ev.occurredAt,
    effectiveEndAt: addMinutes(r.ev.occurredAt, r.defaultSessionMinutes),
    quantity: r.quantity,
    domainUnits: r.domainUnits,
    logMode: 'extended_session',
    source: 'event',
  };
  if (r.ev.planId !== undefined) out.planId = r.ev.planId;
  if (r.methodId !== undefined) out.methodId = r.methodId;
  if (r.methodKind !== undefined) out.methodKind = r.methodKind;
  return out;
}

function sessionKey(r: ResolvedEvent): string {
  return `${r.ev.habitId ?? ''}|${r.methodId ?? r.methodKind ?? ''}`;
}

/**
 * Normalize raw usage events into NormalizedUsage entries.
 * - instant: 1:1 conversion.
 * - session: merges consecutive same-method events whose gap is within mergeWindowMinutes.
 * - extended_session: 1:1 conversion with an inferred end time.
 * - usage_session_ended events override the matching session's effectiveEndAt.
 */
export function normalizeUsageEvents(
  rawEvents: AppEvent[],
  methods: ConsumptionMethodRecord[],
): NormalizedUsage[] {
  const usage = rawEvents.filter((e) => e.type === 'usage_logged').slice().sort(compareEvents);
  const resolved = usage.map((e) => resolveEvent(e, methods));
  const out: NormalizedUsage[] = [];
  const openSessions = new Map<string, NormalizedUsage>();

  for (const r of resolved) {
    if (r.logMode === 'instant') {
      out.push(makeInstant(r));
      continue;
    }
    if (r.logMode === 'extended_session') {
      out.push(makeExtended(r));
      continue;
    }
    // session: try merge with open
    const key = sessionKey(r);
    const open = openSessions.get(key);
    const occMs = new Date(r.ev.occurredAt).getTime();
    if (open) {
      const lastEventMs = new Date(open.occurredAt).getTime();
      const gapMin = (occMs - lastEventMs) / 60_000;
      if (gapMin <= r.mergeWindowMinutes) {
        open.eventIds.push(r.ev.id);
        open.occurredAt = r.ev.occurredAt;
        open.effectiveEndAt = addMinutes(r.ev.occurredAt, r.defaultSessionMinutes);
        open.quantity += r.quantity;
        open.domainUnits = Math.max(open.domainUnits, r.domainUnits);
        open.source = 'merged_session';
        continue;
      }
    }
    const session: NormalizedUsage = {
      eventIds: [r.ev.id],
      habitId: r.ev.habitId ?? '',
      occurredAt: r.ev.occurredAt,
      effectiveStartAt: r.ev.occurredAt,
      effectiveEndAt: addMinutes(r.ev.occurredAt, r.defaultSessionMinutes),
      quantity: r.quantity,
      domainUnits: r.domainUnits,
      logMode: 'session',
      source: 'event',
    };
    if (r.ev.planId !== undefined) session.planId = r.ev.planId;
    if (r.methodId !== undefined) session.methodId = r.methodId;
    if (r.methodKind !== undefined) session.methodKind = r.methodKind;
    out.push(session);
    openSessions.set(key, session);
  }

  applySessionEndOverrides(rawEvents, out);
  return out;
}

function applySessionEndOverrides(rawEvents: AppEvent[], normalized: NormalizedUsage[]): void {
  const ends = rawEvents
    .filter((e) => e.type === 'usage_session_ended')
    .slice()
    .sort(compareEvents);
  for (const ev of ends) {
    const payload = ev.payload as Partial<UsageSessionEndedPayload>;
    if (typeof payload.endedAt !== 'string') continue;
    const endedAtMs = new Date(payload.endedAt).getTime();
    if (!isFinite(endedAtMs)) continue;

    let target: NormalizedUsage | undefined;
    if (typeof payload.refEventId === 'string') {
      target = normalized.find((u) => u.eventIds.includes(payload.refEventId as string));
    }
    if (!target) {
      // Match by habit + method, picking the latest session that brackets endedAt
      // (or the most recent open one if endedAt falls beyond it).
      const candidates = normalized.filter((u) => {
        if (u.logMode === 'instant') return false;
        if (ev.habitId && u.habitId !== ev.habitId) return false;
        if (payload.methodId && u.methodId !== payload.methodId) return false;
        if (payload.methodKind && u.methodKind !== payload.methodKind) return false;
        return new Date(u.effectiveStartAt).getTime() <= endedAtMs;
      });
      target = candidates.sort((a, b) => b.effectiveStartAt.localeCompare(a.effectiveStartAt))[0];
    }
    if (!target) continue;
    if (endedAtMs < new Date(target.effectiveStartAt).getTime()) continue;
    target.effectiveEndAt = new Date(endedAtMs).toISOString();
  }
}

export interface ClassifyNewUsageInput {
  /** Occurrence time of the new (not-yet-stored) usage event. */
  occurredAt: Date;
  habitId: string;
  /** Method picked at log time. May be undefined for legacy/no-method habits. */
  method?: ConsumptionMethodRecord | undefined;
  /** Recently logged events for this habit (last few hours suffices). */
  recentEvents: AppEvent[];
  /** All active methods for the habit, for normalizer lookup. */
  methods: ConsumptionMethodRecord[];
}

export interface ClassifiedNewUsage {
  effectiveStartAt: Date;
  effectiveEndAt: Date;
  domainUnits: number;
  /** True when this tap merged into an open session (no re-classification). */
  merged: boolean;
  logMode: MethodLogMode;
}

const SYNTHETIC_EVENT_ID = '__new_usage__';

/**
 * Classify a not-yet-stored usage event in the context of recent events.
 * Returns effective session bounds, merge status, and domain units so the
 * caller can drive rule engines off normalized usage.
 */
export function classifyNewUsage(input: ClassifyNewUsageInput): ClassifiedNewUsage {
  const occurredAtIso = input.occurredAt.toISOString();
  const synthetic: AppEvent = {
    id: SYNTHETIC_EVENT_ID,
    schemaVersion: 2,
    type: 'usage_logged',
    habitId: input.habitId,
    occurredAt: occurredAtIso,
    localDate: occurredAtIso.slice(0, 10),
    timezoneOffsetMinutes: 0,
    payload: input.method
      ? {
          methodId: input.method.id,
          methodKind: input.method.kind,
          logMode: input.method.defaultLogMode,
          domainUnits: input.method.defaultDomainUnits,
          ...(input.method.defaultSessionMinutes !== undefined ? { defaultSessionMinutes: input.method.defaultSessionMinutes } : {}),
          ...(input.method.mergeWindowMinutes !== undefined ? { mergeWindowMinutes: input.method.mergeWindowMinutes } : {}),
        }
      : {},
    createdAt: occurredAtIso,
    updatedAt: occurredAtIso,
    deviceId: 'classify',
  };
  const all = [...input.recentEvents.filter((e) => e.id !== SYNTHETIC_EVENT_ID), synthetic];
  const normalized = normalizeUsageEvents(all, input.methods);
  const containing = normalized.find((u) => u.eventIds.includes(SYNTHETIC_EVENT_ID));
  const logMode = containing?.logMode ?? input.method?.defaultLogMode ?? 'instant';
  if (!containing) {
    return {
      effectiveStartAt: input.occurredAt,
      effectiveEndAt: input.occurredAt,
      domainUnits: input.method?.defaultDomainUnits ?? 1,
      merged: false,
      logMode,
    };
  }
  const merged = containing.eventIds.length > 1
    && containing.source === 'merged_session'
    && containing.eventIds[0] !== SYNTHETIC_EVENT_ID;
  return {
    effectiveStartAt: new Date(containing.effectiveStartAt),
    effectiveEndAt: new Date(containing.effectiveEndAt),
    domainUnits: containing.domainUnits,
    merged,
    logMode,
  };
}

export function normalizedUsageForHabitInRange(
  habitId: string,
  rawEvents: AppEvent[],
  methods: ConsumptionMethodRecord[],
  range: { fromIso: string; toIso: string },
): NormalizedUsage[] {
  const filtered = rawEvents.filter((e) =>
    e.habitId === habitId
    && e.occurredAt >= range.fromIso
    && e.occurredAt <= range.toIso,
  );
  return normalizeUsageEvents(filtered, methods);
}
